/**
 * Growth Channel ↔ Lead Source Mapping
 *
 * Defines available growth channels and provides helpers for
 * mapping lead sources to channels. Both Growth Channels and
 * Lead Sources import from here to stay aligned.
 */

export interface GrowthChannelDef {
  id: string;
  name: string;
  type: "managed" | "tracked" | "coming_soon";
}

/** Canonical list of growth channels */
export const GROWTH_CHANNELS: GrowthChannelDef[] = [
  { id: "meta-growth-engine", name: "Meta Growth Engine", type: "managed" },
  { id: "angi", name: "Angi", type: "tracked" },
  { id: "thumbtack", name: "Thumbtack", type: "tracked" },
  { id: "google-ads", name: "Google Ads", type: "coming_soon" },
  { id: "referral-engine", name: "Referral Engine", type: "coming_soon" },
];

/** Default mapping: lead source name → growth channel id */
export const DEFAULT_SOURCE_CHANNEL_MAP: Record<string, string> = {
  "Facebook Ads": "meta-growth-engine",
  "Instagram Ads": "meta-growth-engine",
  "Angi Leads": "angi",
  "Angi": "angi",
  "Home Advisor": "angi",
  "Thumbtack": "thumbtack",
  "Google Ads": "google-ads",
  "Referral": "referral-engine",
};

/** Resolve a channel id from a lead source label using canonical mapping + fuzzy fallback. */
export function resolveChannelFromSource(sourceName: string | undefined | null): string | null {
  if (!sourceName) return null;
  const normalized = sourceName.trim();
  if (!normalized) return null;
  if (normalized in DEFAULT_SOURCE_CHANNEL_MAP) {
    return DEFAULT_SOURCE_CHANNEL_MAP[normalized] ?? null;
  }
  return suggestChannel(normalized);
}

/** Suggest a channel for a source name (fuzzy match) */
export function suggestChannel(sourceName: string): string | null {
  const lower = sourceName.toLowerCase();
  if (lower.includes("facebook") || lower.includes("meta") || lower.includes("instagram")) return "meta-growth-engine";
  if (lower.includes("angi") || lower.includes("homeadvisor")) return "angi";
  if (lower.includes("thumbtack")) return "thumbtack";
  if (lower.includes("google")) return "google-ads";
  if (lower.includes("referral")) return "referral-engine";
  return null;
}

/** Get channel definition by id */
export function getChannelById(id: string): GrowthChannelDef | undefined {
  return GROWTH_CHANNELS.find(ch => ch.id === id);
}

/** Get selectable channels (managed + tracked only) */
export function getSelectableChannels(): GrowthChannelDef[] {
  return GROWTH_CHANNELS.filter(ch => ch.type !== "coming_soon");
}
